import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceConfigurator = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [estimatedTimeline, setEstimatedTimeline] = useState(0);

  const services = [
    {
      id: 'data-strategy',
      name: 'Data Strategy Development',
      description: 'Comprehensive roadmap for data transformation',
      basePrice: 15000,
      duration: 6,
      icon: 'Target',
      benefits: ['Strategic alignment', 'Clear roadmap', 'Risk mitigation']
    },
    {
      id: 'analytics-implementation',
      name: 'Analytics Implementation',
      description: 'End-to-end analytics platform deployment',
      basePrice: 25000,
      duration: 12,
      icon: 'BarChart3',
      benefits: ['Real-time insights', 'Automated reporting', 'Data visualization']
    },
    {
      id: 'governance-framework',
      name: 'Governance Framework Design',
      description: 'Data governance policies and procedures',
      basePrice: 12000,
      duration: 8,
      icon: 'Shield',
      benefits: ['Compliance assurance', 'Data quality', 'Risk management']
    },
    {
      id: 'team-training',
      name: 'Team Training & Development',
      description: 'Train your team to think like a CDO',
      basePrice: 8000,
      duration: 4,
      icon: 'Users',
      benefits: ['Skill enhancement', 'Best practices', 'Knowledge transfer']
    },
    {
      id: 'fractional-cdo',
      name: 'Fractional CDO Service',
      description: 'Executive-level data leadership on demand',
      basePrice: 20000,
      duration: 12,
      icon: 'Crown',
      benefits: ['Executive guidance', 'Strategic oversight', 'Ongoing support']
    }
  ];

  useEffect(() => {
    const total = selectedServices?.reduce((sum, serviceId) => {
      const service = services?.find(s => s?.id === serviceId);
      return sum + (service ? service?.basePrice : 0);
    }, 0);
    setTotalInvestment(total);

    const maxDuration = selectedServices?.reduce((max, serviceId) => {
      const service = services?.find(s => s?.id === serviceId);
      return Math.max(max, service ? service?.duration : 0);
    }, 0);
    setEstimatedTimeline(maxDuration);
  }, [selectedServices]);

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev?.includes(serviceId) 
        ? prev?.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const isSelected = (serviceId) => selectedServices?.includes(serviceId);

  return (
    <div className="bg-white rounded-xl shadow-elevation border border-border p-8">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-primary mb-4">
          Build Your Custom Solution Package
        </h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Select the services that align with your data transformation goals. Our configurator will calculate investment and timeline estimates.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {services?.map((service) => (
          <div
            key={service?.id}
            className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 ${
              isSelected(service?.id)
                ? 'border-secondary bg-secondary/5' :'border-border hover:border-secondary/50'
            }`}
            onClick={() => toggleService(service?.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg transition-all duration-300 hover:scale-110 ${
                isSelected(service?.id) ? 'bg-secondary text-white' : 'bg-surface text-primary'
              }`}>
                <Icon name={service?.icon} size={24} />
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                isSelected(service?.id)
                  ? 'border-secondary bg-secondary' :'border-border'
              }`}>
                {isSelected(service?.id) && (
                  <Icon name="Check" size={16} color="white" />
                )}
              </div>
            </div>

            <h4 className="font-semibold text-primary mb-2">{service?.name}</h4>
            <p className="text-sm text-muted-foreground mb-4">{service?.description}</p>

            <div className="space-y-2 mb-4">
              {service?.benefits?.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-secondary" />
                  <span className="text-sm text-muted-foreground">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">From €{service?.basePrice?.toLocaleString()}</span>
              <span className="text-muted-foreground">{service?.duration} weeks</span>
            </div>
          </div>
        ))}
      </div>
      {selectedServices?.length > 0 && (
        <div className="bg-surface rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {selectedServices?.length}
              </div>
              <div className="text-sm text-muted-foreground">Services Selected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary mb-1">
                €{totalInvestment?.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Total Investment</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent mb-1">
                {estimatedTimeline} weeks
              </div>
              <div className="text-sm text-muted-foreground">Estimated Timeline</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              iconName="Calculator"
              iconPosition="left"
              onClick={() => window.location.href = '/roi-calculator-suite-value-demonstration-tools'}
              className="hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Calculate ROI
            </Button>
            <Button
              variant="default"
              iconName="Calendar"
              iconPosition="left"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 active:scale-95 transition-all duration-200"
              onClick={() => window.location.href = '/contact-multi-channel-engagement-hub'}
            >
              Discuss Your Package
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceConfigurator;